'use client';
import React from 'react';
import { ProductVariation } from '@typings/product';
import { useCurrencyString } from '@hooks/useCurrencyString';
import { classNames } from '@repo/ui/src/utils/classNames';
import { ProductProps } from '@repo/ui/src/types';

type VariantType = keyof typeof VARIANT;
type AlignType = keyof typeof ALIGNMENT;

interface Props {
  product: ProductProps | ProductVariation;
  flipOrder?: boolean;
  className?: string;
  align?: AlignType;
  color?: 'white';
  variant?: VariantType | false;
  component?: any;
  gutterBottom?: boolean;
  outOfStock?: boolean;
}

const ALIGNMENT = {
  center: 'tw-justify-center tw-text-center',
  left: 'tw-justify-left tw-text-left',
  right: 'tw-justify-right tw-text-right',
};

const VARIANT = {
  h3: 'tra-text-web-heading-3',
  h4: 'tra-text-web-heading-4',
  h5: 'tra-text-web-heading-5',
  h6: 'tra-text-web-heading-6',
  h7: 'tra-text-web-heading-7',
  small: 'tw-text-xs',
  sub: 'tra-text-web-sub-1',
};

const ProductPrice = ({
  product,
  flipOrder = false,
  align,
  color,
  variant,
  component: Component = 'p',
  gutterBottom,
  className,
  outOfStock,
}: Props) => {
  const getCurrencyString = useCurrencyString();

  if (!product || product.price <= 0) return null;

  return (
    <Component
      data-testid="pc-price-component"
      className={classNames(
        'tw-flex tw-gap-x-2',
        color && 'tw-text-white',
        outOfStock && 'tw-text-surface-450',
        variant ? VARIANT[variant] : 'tra-text-web-body-xl',
        align && ALIGNMENT[align],
        gutterBottom && 'tw-pb-4',
        className,
      )}
    >
      <span className="tw-flex tw-justify-center tw-items-center">
        {!flipOrder && getCurrencyString(product.price)}
      </span>
      {product.discount > 0 && (
        <del
          className={classNames(
            'tw-pr-0 tw-text-text-300 tw-line-through tw-inline',
            flipOrder && 'tw-pl-0 tw-pr-2',
            color && 'tw-text-white',
            outOfStock && 'tw-text-surface-450',
            variant ? VARIANT[variant] : 'tra-text-web-body-xl',
          )}
        >
          <span className="sr-only">Original Price {getCurrencyString(product.originalPrice)}</span>
          {getCurrencyString(product.originalPrice)}
        </del>
      )}
      {flipOrder && getCurrencyString(product.price)}
      <span className="sr-only">Sale Price {getCurrencyString(product.price)}</span>
    </Component>
  );
};

export default ProductPrice;
